import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/shared/components/ui/card';
import { isDevelopment } from '@/shared/lib/env';
import { AlertVariant } from '@/shared/types/alert.types';
import { ButtonVariant } from '@/shared/types/button.types';

interface ErrorFallbackProps {
  readonly error: Error;
  readonly resetErrorBoundary: () => void;
  readonly showDetails?: boolean;
}

/**
 * Error fallback component displayed when an error boundary catches an error
 * Features:
 * - User-friendly error message
 * - Retry button to reset error boundary
 * - Home navigation option
 * - Optional error details for debugging
 * - Theme-aware design
 */
export function ErrorFallback({
  error,
  resetErrorBoundary,
  showDetails = isDevelopment,
}: ErrorFallbackProps) {
  const handleGoHome = () => {
    globalThis.location.href = '/';
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle
                className="size-6 text-destructive"
                aria-hidden="true"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Нещо се обърка
              </h1>
              <p className="text-sm text-muted-foreground">
                Възникна неочаквана грешка
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert variant={AlertVariant.Destructive}>
            <AlertDescription>
              Приложението срещна грешка и не можа да продължи. Моля, опитайте
              отново или се свържете с поддръжката, ако проблемът продължава.
            </AlertDescription>
          </Alert>

          {showDetails && (
            <div className="rounded-lg border border-border bg-muted p-4">
              <p className="mb-2 text-sm font-semibold text-foreground">
                Технически детайли:
              </p>
              <pre className="overflow-x-auto text-xs text-muted-foreground">
                <code>{error.message}</code>
              </pre>
              {error.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    Stack trace
                  </summary>
                  <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                    <code>{error.stack}</code>
                  </pre>
                </details>
              )}
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button
            onClick={resetErrorBoundary}
            className="flex-1"
            variant={ButtonVariant.Default}
            aria-label="Опитай отново"
          >
            <RefreshCw className="mr-2 size-4" aria-hidden="true" />
            Опитай отново
          </Button>
          <Button
            onClick={handleGoHome}
            variant={ButtonVariant.Outline}
            className="flex-1"
            aria-label="Към начална страница"
          >
            <Home className="mr-2 size-4" aria-hidden="true" />
            Начална страница
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
